import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import WikiPageEditor from '../views/WikiPageEditor.vue'
import LintReview from '../views/LintReview.vue'
import SynthesisReview from '../views/SynthesisReview.vue'
import AdminTokens from '../views/AdminTokens.vue'
import AdminShares from '../views/AdminShares.vue'
import AdminExpertConsultations from '../views/AdminExpertConsultations.vue'
import AdminCalendar from '../views/AdminCalendar.vue'
import AdminCv from '../views/AdminCv.vue'
import AdminAgentRepos from '../views/AdminAgentRepos.vue'
import AdminAgentHostStats from '../views/AdminAgentHostStats.vue'
import AdminProductOwnerRuns from '../views/AdminProductOwnerRuns.vue'
import PromptLibrary from '../views/PromptLibrary.vue'
import AdminReminders from '../views/AdminReminders.vue'
import AdminLists from '../views/AdminLists.vue'
import UsageView from '../views/UsageView.vue'
import RetrievalBenchView from '../views/RetrievalBenchView.vue'
import OutputSkillsView from '../views/OutputSkillsView.vue'
import AgentView from '../views/AgentView.vue'
import AdminChannels from '../views/AdminChannels.vue'
import LogView from '../views/LogView.vue'
import { useSessionStore } from '../stores/session'
import { GENERAL_BUCKET, GENERAL_BUCKET_ALIAS } from '../composables/categoryBucket'

// Exported so a coverage test can walk every route without duplicating
// this list (see lib/navConfig.spec.ts) — a route added here with no
// matching nav entry is a regression that test exists to catch.
export const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: HomeView },
  // Authoring routes. Both render the same editor view; the view branches
  // on route.name to decide create-vs-edit. Listed before the catch-all
  // deep-link route so `/p/new` doesn't resolve as a page path.
  { path: '/p/new', name: 'wikiPageNew', component: WikiPageEditor },
  { path: '/p/:path(.*)/edit', name: 'wikiPageEdit', component: WikiPageEditor },
  // Legacy alias for the General bucket — the sidebar's old internal
  // sentinel. CategoryView handles both keys, but normalising the URL
  // keeps shared/bookmarked links pointing at the canonical form.
  // Listed before the generic /c/:category so the redirect wins.
  { path: `/c/${GENERAL_BUCKET_ALIAS}`, redirect: `/c/${GENERAL_BUCKET}` },
  // Memory is consolidated to the single /memory destination, so
  // the `/c/memory` category page (e.g. a memory page's breadcrumb crumb,
  // or a bookmark) redirects to the scope-grouped view rather than a
  // second flat list. Listed before the generic /c/:category so it wins.
  { path: '/c/memory', redirect: '/memory' },
  // Category landing page — the destination the meta-bar breadcrumb's
  // category crumb links to. Renders as a list of every page in the
  // category inside HomeView's main pane (sidebar stays put).
  { path: '/c/:category', name: 'wikiCategory', component: HomeView },
  // Admin-only Lint review queue. Auth gating happens in the
  // beforeEach guard below so a non-admin URL-typing the route gets
  // bounced to /. The view itself trusts the gate and skips its own
  // role checks.
  { path: '/lint', name: 'lintReview', component: LintReview, meta: { requiresAdmin: true } },
  // Admin-only Synthesis review queue. Same auth-gate pattern
  // as /lint — the weekly Synthesis pass surfaces candidate new pages
  // and the admin reviews them here. Non-admins get bounced to /.
  { path: '/synthesis', name: 'synthesisReview', component: SynthesisReview, meta: { requiresAdmin: true } },
  // Run a memory-inheriting output skill to draft a deliverable.
  // Admin-only like the other owner-facing tools.
  { path: '/output-skills', name: 'outputSkills', component: OutputSkillsView, meta: { requiresAdmin: true } },
  // Admin-only web chat to drive cloud agent sessions — the web
  // sibling of the Discord agent channel. Output streams via ActionCable.
  // Optional :threadId so an open session is deep-linkable and restored on
  // reload — /agent is the new-session form, /agent/<id> opens that session.
  { path: '/agent/:threadId?', name: 'agent', component: AgentView, meta: { requiresAdmin: true } },
  // Admin-only live feed of shared agent channels (#1835).
  { path: '/channels', name: 'channels', component: AdminChannels, meta: { requiresAdmin: true } },
  // Admin-only cost / token-usage dashboard. Same auth-gate
  // pattern as /lint and /synthesis — the view exposes per-model
  // token + dollar spend so non-admins shouldn't see it.
  { path: '/usage', name: 'usage', component: UsageView, meta: { requiresAdmin: true } },
  // The brain's activity log (wiki/log.md parsed into a day-grouped feed).
  // Open to every signed-in user, like the raw log page it replaces in nav.
  { path: '/log', name: 'log', component: LogView },
  // Admin-only memory view. Routes through
  // HomeView — the same shell `/c/:category` uses — so the catalog
  // sidebar stays visible; HomeView branches on `route.name === 'memory'`
  // to render MemoryView in the main pane. The memory API is admin-only,
  // so non-admins get bounced to / by the beforeEach guard.
  { path: '/memory', name: 'memory', component: HomeView, meta: { requiresAdmin: true } },
  // One memory entry as a full page (same HomeView shell and admin gate).
  {
    path: '/memory/:project/:name',
    name: 'memoryEntry',
    component: HomeView,
    meta: { requiresAdmin: true },
  },
  // Admin-only API token management. Same auth gate as /lint; same
  // pattern (route meta + beforeEach redirect). The /admin/* namespace
  // is the home for future admin-only views.
  { path: '/admin/tokens', name: 'adminTokens', component: AdminTokens, meta: { requiresAdmin: true } },
  // Admin-only management of active public share links: list +
  // revoke. New links are created from a page's Share action.
  { path: '/admin/shares', name: 'adminShares', component: AdminShares, meta: { requiresAdmin: true } },
  {
    path: '/admin/expert-consultations',
    name: 'adminExpertConsultations',
    component: AdminExpertConsultations,
    meta: { requiresAdmin: true },
  },
  // Admin-only management of the calendar heartbeat's OAuth credentials:
  // set the Google client id/secret without a redeploy, connect /
  // disconnect, and see per-provider poll health.
  { path: '/admin/calendar', name: 'adminCalendar', component: AdminCalendar, meta: { requiresAdmin: true } },
  // Admin-only Live CV: the owner profile form now; generation, review,
  // and publishing land in later slices of #1674.
  { path: '/admin/cv', name: 'adminCv', component: AdminCv, meta: { requiresAdmin: true } },
  // Admin-only agent repo management: view, add, and manage repos the
  // cloud agent can work on, with encrypted PAT storage (no SSH / redeploy).
  { path: '/admin/agent-repos', name: 'adminAgentRepos', component: AdminAgentRepos, meta: { requiresAdmin: true } },
  {
    path: '/admin/host-stats',
    name: 'adminAgentHostStats',
    component: AdminAgentHostStats,
    meta: { requiresAdmin: true },
  },
  // Admin-only read-only run history for the autonomous product-owner
  // heartbeat (#895/#916) — track activity during the initial rollout.
  {
    path: '/admin/product-owner-runs',
    name: 'adminProductOwnerRuns',
    component: AdminProductOwnerRuns,
    meta: { requiresAdmin: true },
  },
  // Admin-only canned-prompt curation: global or repo-scoped prompts
  // the /agent composer's new-session picker offers instead of free typing.
  { path: '/admin/agent-prompts', name: 'adminAgentPrompts', component: PromptLibrary, meta: { requiresAdmin: true } },
  { path: '/reminders', name: 'reminders', component: AdminReminders, meta: { requiresAdmin: true } },
  { path: '/lists', name: 'lists', component: AdminLists, meta: { requiresAdmin: true } },
  // Admin-only retrieval-benchmark report. Same auth-gate pattern
  // as /usage — renders the committed docs/retrieval_bench.md summary so
  // operators can read P@5/R@5/MRR without opening the file on the box.
  {
    path: '/admin/retrieval-bench',
    name: 'retrievalBench',
    component: RetrievalBenchView,
    meta: { requiresAdmin: true },
  },
  // Deep-link route for Discord citations and any external link that wants
  // to jump straight to a specific wiki page. The viewer reads the path
  // from `route.params.path` and selects/loads that WikiPage on mount.
  { path: '/p/:path(.*)', name: 'wikiPage', component: HomeView },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Prime the session store on the first navigation so App.vue can decide
// whether to show the sign-in form or the authenticated shell. Auth gating
// itself lives in App.vue — we don't redirect unauthenticated users to a
// `/sign_in` URL anymore, we render the form in-place.
router.beforeEach(async (to) => {
  const session = useSessionStore()
  if (!session.checked) await session.fetchCurrentUser()
  // Admin gate for routes that opted in via meta. Non-admins get bounced
  // to /, mirroring how the API returns 403 — the SPA refuses to render
  // a queue it can't even fetch.
  if (to.meta.requiresAdmin && !session.currentUser?.admin) return '/'
})
